//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useState, useEffect, useContext, useRef } from 'react';
import { makeStyles, Radio } from '@material-ui/core';
import { useHistory } from 'react-router-dom';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
// import { unsubscribeRealtimeUpdates } from '../../system/firebase';
import { PATH_HOME } from '../../system/constants';
// import { LayoutContext, AppContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';
import { BASE_URL, DASHBOARD_MY_CALENDAR } from '../../common/constants';
import { getTierTitle, getReasonLov, getTiersLov } from '../../common/lovHelper';
import { LayoutContext, AppContext } from '../../common/contexts';
import {
    CONFIRM_DIALOG_ELIGIBLE_FOR_REFUND_HEADING,
    CONFIRM_DIALOG_ELIGIBLE_FOR_REFUND_MESSAGE,
    CONFIRM_DIALOG_ACCOUNT_CANCELATION_HEADING,
    CONFIRM_DIALOG_ACCOUNT_CANCELLATION_MESSAGE,
} from '../../common/constants';
import { retrieveStripSubscription } from '../../common/services/paymentServices';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import { cancelSubscription } from '../../common/services/empMySettingsServices';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdTypography from '../../common/elemental/AfitdTypography';
import AfitdEditDialog from '../../common/elemental/AfitdEditDialog';
import AfitdAutocomplete from '../../common/elemental/AfitdAutocompleteA1';
import AfitdTextField from '../../common/elemental/AfitdTextField';
import AfitdMessageDialog from '../../common/elemental/AfitdMessageDialog';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
import DowngradeDialog from './DowngradeDialog';
import ConfirmDowngradeDialog from './ConfirmDowngradeDialog';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import cx from 'clsx';
import { differenceInHours } from 'date-fns';
import _get from 'lodash/get';
import _noop from 'lodash/noop';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    const { subscribedTier, trialAvailable } = params;
    const subtitleHidden = subscribedTier === 1 || trialAvailable;
    return makeStyles(theme => ({
        currentTierContainer: { textAlign: 'center', background: 'rgb(240,242,246)', padding: 10, borderRadius: 4, margin: '20px 0' },
        optionsContainer: { display: 'flex', borderRadius: 4, margin: '10px 0', alignItems: 'start' },
        innerOptionsContainer: { flexGrow: 1 },
        subtitle: { fontWeight: 'bold', display: subtitleHidden ? 'none' : undefined },

        lineSeprator: { borderTop: '2px solid rgb(240,242,246)', margin: '20px 0' },

        currentTierNameText: { fontWeight: 'bold' },

        optionTitle: { fontWeight: 'bold', marginTop: 7 },
        optionQuestion: { fontWeight: 'bold', margin: '20px 0 10px 0' },
        optionSubTitle: { color: 'rgb(155,158,156)', marginBottom: 20 },
        hidden: { display: 'none' },
    }));
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        classes,
        props,
        data,
        setData,
        selectedRadioOption,
        setSelectedRadioOption,
        handleClickSaveButton,
        downgradeModalFlag,
        setDowngradeModalFlag,
        tiers,
        // trialAvailable,
        myRef,
        handleCancelButton,
        confirmDowngradeModalOpen,
        setConfirmDowngradeModalOpen,
        members,
        eligibleRefundMessageDialogOpen,
        // setEligibleRefundMessageDialogOpen,
        // hoursDifference,
        cancelScheduledMessageDialogOpen,
        // setCancelScheduledMessageDialogOpen,
        stripeSubscription,
        // setStripeSubscription,
        handleClickEligibleRefundMessageDialogOk,
        handleClickCancelScheduledMessageDialogOk,
        handleCloseDialog,
        cancelLoading,
    } = params;
    const { open, onClose = _noop, subscribedTier } = props;
    const { hidden: hiddenCls } = classes;
    const { feedback, reason, newTier } = data;

    const isCancelSelected = selectedRadioOption === 'cancel';
    const isDowngradeProtected = subscribedTier === 1;
    let isDirty = !isCancelSelected || (isCancelSelected && reason);
    if (reason === 'Other' && feedback === '') isDirty = false;

    const selectedTier = Number(newTier[newTier.length - 1]);

    const saveButtonDisabled = !stripeSubscription || !isDirty;

    const defns = {
        container: {
            open,
            onClose: handleCloseDialog,
            label: `Thinking of changing your subscription?`,
            actionButtonDefns: {
                save: {
                    disabled: saveButtonDisabled || cancelLoading,
                    onClick: handleClickSaveButton,
                    label: isCancelSelected ? 'Cancel Plan' : 'Downgrade Plan',
                    loading: cancelLoading,
                },
            },
        },
        subtitle: { variant: 'body2', value: 'Hold up, there might be a better way!' },
        currentTierText: { variant: 'body1' },
        currentTierNameText: { variant: 'body1', component: 'span', value: ` ${getTierTitle(subscribedTier)}` },

        downgradeOption: { className: isCancelSelected && hiddenCls },

        downgradeRadioButton: { disableRipple: true, checked: !isCancelSelected, onChange: () => setSelectedRadioOption('downgrade'), disabled: isDowngradeProtected },
        cancelRadioButton: {
            disableRipple: true,
            checked: isCancelSelected,
            onChange: () => setSelectedRadioOption('cancel'),
            onClick: () => {
                setTimeout(() => {
                    myRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 100);
            },
        },

        optionTitle: { variant: 'body1' },
        optionSubTitle: { variant: 'body2' },
        otherTiers: {
            disableClearable: true,
            options: getTiersLov(subscribedTier),
            value: newTier,
            disabled: isDowngradeProtected,
            placeholder: `Tier ${subscribedTier}`,
            onChange: (i, evt) => setData({ ...data, newTier: evt }),
        },

        exitInterviewContainer: { className: !isCancelSelected && hiddenCls },
        optionQuestion: { variant: 'body2' },
        reason: { onChange: (i, evt) => setData({ ...data, reason: evt }), options: getReasonLov(), value: reason },
        feedback: {
            rows: 4,
            multiline: true,
            value: feedback,
            placeholder: 'Share your feedback',
            onChange: evt => setData({ ...data, feedback: evt.target.value }),
        },
        confirmDowngradeModal: {
            subscribedTier,
            selectedTier,
            onClose: handleCancelButton,
            onConfirm: () => setDowngradeModalFlag(true),
            open: confirmDowngradeModalOpen,
        },
        eligibleRefundMessageDialog: {
            title: CONFIRM_DIALOG_ELIGIBLE_FOR_REFUND_HEADING,
            message: CONFIRM_DIALOG_ELIGIBLE_FOR_REFUND_MESSAGE,
            open: eligibleRefundMessageDialogOpen,
            onClose: handleClickEligibleRefundMessageDialogOk,
        },
        cancelScheduledMessageDialog: {
            title: CONFIRM_DIALOG_ACCOUNT_CANCELATION_HEADING,
            message: CONFIRM_DIALOG_ACCOUNT_CANCELLATION_MESSAGE,
            open: cancelScheduledMessageDialogOpen,
            onClose: handleClickCancelScheduledMessageDialogOk,
        },
        downgradeModal: {
            openDowngradeModal: downgradeModalFlag,
            closeDowngradeModal: () => setDowngradeModalFlag(false),
            setIsDirtyFlagNull: () => {
                setConfirmDowngradeModalOpen(false);
                onClose();
            },
            selectedTier,
            tiers,
            hideSeatReductionModal: downgradeModalFlag && (selectedTier === 1 || (selectedTier === 2 && members.length <= 1)),
        },
    };

    setClassNameInDefns(defns, classes);
    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
export default function CancelSubscriptionDialog(props) {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const appContext = useContext(AppContext);
    const { currentEmployer, employerId, userId, sys } = appContext;

    const layoutContext = useContext(LayoutContext);
    const { setSnackbarMessage, setSnackbarErrorMessage, setCurrentDashboard } = layoutContext;

    const companyName = _get(currentEmployer, 'companyName', '');
    const subscriptionDt = _get(currentEmployer, 'subscriptionDt', '');
    const stripeSubId = _get(currentEmployer, 'stripeSubId');
    const stripeCusCreateDt = _get(currentEmployer, 'stripeCusCreateDt');
    const stripeAdditionalSeatSubId = _get(currentEmployer, 'stripeAdditionalSeatSubId');
    const members = _get(currentEmployer, 'members', []);
    const profilePicUrl = _get(currentEmployer, 'profilePicUrl');
    const tier = _get(currentEmployer, 'tier');
    const trialAvailable = _get(currentEmployer, 'trialAvailable');
    const tiers = _get(sys, 'tiers', []);

    const currentMember = members.find(el => el.userId === userId);
    const email = _get(currentMember, 'email', '');
    const firstName = _get(currentMember, 'firstName', '');
    const lastName = _get(currentMember, 'lastName', '');

    const myRef = useRef(null);
    const hoursDifference = differenceInHours(new Date(), new Date(subscriptionDt));

    const history = useHistory();

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const { open, subscribedTier, onClose = _noop, setIsDirtyFlagNull } = props;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [downgradeModalFlag, setDowngradeModalFlag] = useState(false);
    const [eligibleRefundMessageDialogOpen, setEligibleRefundMessageDialogOpen] = useState(false);
    const [cancelScheduledMessageDialogOpen, setCancelScheduledMessageDialogOpen] = useState(false);
    const [confirmDowngradeModalOpen, setConfirmDowngradeModalOpen] = useState(false);
    const [data, setData] = useState({ reason: '', feedback: '', newTier: '' });
    const [selectedRadioOption, setSelectedRadioOption] = useState('downgrade');
    const [stripeSubscription, setStripeSubscription] = useState();
    const [cancelLoading, setCancelLoading] = useState(false);

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------
    const { reason, feedback } = data;
    const firstValueOnTierLov = _get(getTiersLov(subscribedTier), 0, []);

    // const stripeSubscriptionCreated = stripeSubscription?.created;
    // const stripeSubscriptionCreateDt = stripeSubscriptionCreated ? new Date(stripeSubscriptionCreated * 1000) : null;

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    useEffect(() => {
        subscribedTier === 1 || trialAvailable ? setSelectedRadioOption('cancel') : setSelectedRadioOption('downgrade');
        firstValueOnTierLov && setData({ ...data, newTier: firstValueOnTierLov });

        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subscribedTier, trialAvailable]);

    // load Stripe subscription data for Tier
    useEffect(() => {
        const retrieveSubscription = async () => {
            const retrieveSubscriptionResp = await retrieveStripSubscription({ subscriptionId: stripeSubId });
            retrieveSubscriptionResp && setStripeSubscription(retrieveSubscriptionResp.data);
        };
        if (open && stripeSubId) {
            retrieveSubscription();
        } else {
            setStripeSubscription(null);
        }
    }, [open, stripeSubId]);

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    const handleCancelButton = () => {
        setConfirmDowngradeModalOpen(false);
        setCurrentDashboard(DASHBOARD_MY_CALENDAR);
    };

    const handleClickSaveButton = async () => {
        if (selectedRadioOption === 'cancel') {
            setCancelLoading(true);
            const hoursDifference = differenceInHours(new Date(), stripeCusCreateDt);
            const isEligibleRefund = hoursDifference < 48;

            const success = await cancelSubscription({
                isRefund: isEligibleRefund,
                stripeSubId,
                stripeAdditionalSeatSubId,
                name: `${firstName} ${lastName}`,
                email,
                employerId,
                tier,
                profilePicUrl,
                feedback,
                reason,
                userId,
                companyName,
                setSnackbarMessage,
                setSnackbarErrorMessage,
            });
            setCancelLoading(false);

            if (success) {
                if (isEligibleRefund) history.push({ pathname: `${BASE_URL}${PATH_HOME}`, state: { messageMode: 'cancel_plan_successful' } });
                else setCancelScheduledMessageDialogOpen(true);
            }
            ////
        } else {
            setConfirmDowngradeModalOpen(true);
        }
    };

    const handleClickEligibleRefundMessageDialogOk = () => {
        setEligibleRefundMessageDialogOpen(false);
        handleCloseDialog();
    };

    const handleClickCancelScheduledMessageDialogOk = () => {
        setCancelScheduledMessageDialogOpen(false);
        handleCloseDialog();
    };

    const handleCloseDialog = () => {
        setIsDirtyFlagNull && setIsDirtyFlagNull();
        setData({ reason: '', feedback: '', newTier: '' });
        setEligibleRefundMessageDialogOpen(false);
        onClose && onClose();
    };

    // const initiateRefund = async () => {
    //     // await unsubscribe({
    //     //     data: { name: `${firstName} ${lastName}`, email, employerId, feedback, reason, userId, companyName },
    //     //     employerId,
    //     //     setSnackbarMessage,
    //     //     setSnackbarErrorMessage,
    //     // });
    //     const success = await cancelSubscription({
    //         isRefund: true,
    //         stripeSubId,
    //         stripeAdditionalSeatSubId,
    //         name: `${firstName} ${lastName}`,
    //         email,
    //         employerId,
    //         feedback,
    //         reason,
    //         userId,
    //         companyName,
    //         setSnackbarMessage,
    //         setSnackbarErrorMessage,
    //     });

    //     if (!success) {
    //         // successful
    //         setIsDirtyFlagNull();
    //         setData({ reason: '', feedback: '', newTier: '' });
    //         setEligibleRefundMessageDialogOpen(false);
    //         onClose();
    //     }
    // };

    // const scheduleSubscriptionCancellation = async () => {
    //     // await unsubscribe({
    //     //      name: `${firstName} ${lastName}`, email, employerId, feedback, reason, userId, companyName,
    //     //     employerId,
    //     //     setSnackbarMessage,
    //     //     setSnackbarErrorMessage,
    //     // });
    //     const success = await cancelSubscription({
    //         isRefund: false,
    //         stripeSubId,
    //         stripeAdditionalSeatSubId,
    //         name: `${firstName} ${lastName}`,
    //         email,
    //         employerId,
    //         feedback,
    //         reason,
    //         userId,
    //         companyName,
    //         setSnackbarMessage,
    //         setSnackbarErrorMessage,
    //     });

    //     if (!success) {
    //         setIsDirtyFlagNull();
    //         setData({ reason: '', feedback: '', newTier: '' });
    //         setEligibleRefundMessageDialogOpen(false);
    //         onClose();
    //     }
    // };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const classes = useStyles({ subscribedTier, trialAvailable })();
    const defns = getDefns({
        classes,
        props,
        data,
        setData,
        selectedRadioOption,
        setSelectedRadioOption,
        handleClickSaveButton,
        downgradeModalFlag,
        setDowngradeModalFlag,
        tiers,
        trialAvailable,
        myRef,
        handleCancelButton,
        confirmDowngradeModalOpen,
        setConfirmDowngradeModalOpen,
        members,
        eligibleRefundMessageDialogOpen,
        setEligibleRefundMessageDialogOpen,
        hoursDifference,
        cancelScheduledMessageDialogOpen,
        setCancelScheduledMessageDialogOpen,
        stripeSubscription,
        setStripeSubscription,
        handleClickEligibleRefundMessageDialogOk,
        handleClickCancelScheduledMessageDialogOk,
        handleCloseDialog,
        cancelLoading,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <AfitdEditDialog {...defns.container}>
            <AfitdTypography {...defns.subtitle} />
            <div {...defns.currentTierContainer}>
                <AfitdTypography {...defns.currentTierText}>
                    Your current subscription is
                    <AfitdTypography {...defns.currentTierNameText} />
                </AfitdTypography>
            </div>
            <div {...defns.downgradeOption}>
                <div {...defns.optionsContainer}>
                    <Radio {...defns.downgradeRadioButton} />
                    <div {...defns.innerOptionsContainer}>
                        <AfitdTypography {...defns.optionTitle}>Downgrade Plan</AfitdTypography>
                        <AfitdTypography {...defns.optionSubTitle}>Choose a new subscription</AfitdTypography>
                        <AfitdAutocomplete {...defns.otherTiers} />
                    </div>
                </div>
            </div>
            <div {...defns.lineSeprator} />
            <div {...defns.optionsContainer}>
                <Radio {...defns.cancelRadioButton} />
                <div {...defns.innerOptionsContainer}>
                    <AfitdTypography {...defns.optionTitle}>Cancel your subscription</AfitdTypography>
                    <AfitdTypography {...defns.optionSubTitle}>Stop recurring payments effective immediately.</AfitdTypography>

                    <div {...defns.exitInterviewContainer}>
                        <AfitdTypography {...defns.optionTitle}>Employer Exit Interview </AfitdTypography>
                        <AfitdTypography {...defns.optionQuestion}>Why have you decided to cancel your subscription? *</AfitdTypography>
                        <AfitdAutocomplete {...defns.reason} />
                        {reason === 'Other' && (
                            <>
                                <AfitdTypography {...defns.optionQuestion}>Additional Feedback</AfitdTypography>
                                <AfitdTextField {...defns.feedback} />
                            </>
                        )}
                        <div ref={myRef} />
                    </div>
                </div>
            </div>
            <DowngradeDialog {...defns.downgradeModal} />
            <ConfirmDowngradeDialog {...defns.confirmDowngradeModal} />
            <AfitdMessageDialog {...defns.eligibleRefundMessageDialog} />
            <AfitdMessageDialog {...defns.cancelScheduledMessageDialog} />
        </AfitdEditDialog>
    );
}
